import React, { useState, useEffect } from 'react';
import { X, Link2, ArrowRight } from 'lucide-react';
import Button from '../ui/Button';

interface AddRelationshipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (relationshipData: NewRelationshipData) => void;
  sourceNode: { id: string; label: string } | null;
  targetNode: { id: string; label: string } | null;
}

export interface NewRelationshipData {
  relationshipType: string;
  cardinality: 'one_to_one' | 'one_to_many' | 'many_to_one' | 'many_to_many';
  description: string;
  sourceColumn?: string;
  targetColumn?: string;
}

const CARDINALITY_OPTIONS = [
  { value: 'one_to_one', label: '1:1', description: 'One to One' },
  { value: 'one_to_many', label: '1:N', description: 'One to Many' },
  { value: 'many_to_one', label: 'N:1', description: 'Many to One' },
  { value: 'many_to_many', label: 'N:N', description: 'Many to Many' },
];

const COMMON_RELATIONSHIP_TYPES = [
  'HAS',
  'BELONGS_TO',
  'CONTAINS',
  'REFERENCES',
  'CREATED_BY',
  'MANAGES',
  'OWNS',
  'SUPPLIES',
  'PRODUCES',
  'SHIPS_TO',
  'REPORTS_TO',
  'ASSOCIATED_WITH',
];

export const AddRelationshipModal: React.FC<AddRelationshipModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  sourceNode,
  targetNode,
}) => {
  const [relationshipType, setRelationshipType] = useState('');
  const [cardinality, setCardinality] = useState<NewRelationshipData['cardinality']>('one_to_many');
  const [description, setDescription] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRelationshipType('');
      setCardinality('one_to_many');
      setDescription('');
    }
  }, [isOpen]);

  // Generate suggested relationship type based on node names
  useEffect(() => {
    if (sourceNode && targetNode && !relationshipType) {
      // Auto-suggest a relationship type based on common patterns
      const source = sourceNode.label.toUpperCase();
      const target = targetNode.label.toUpperCase();

      if (target.includes('ORDER')) {
        setRelationshipType('PLACED');
      } else if (target.includes('PRODUCT')) {
        setRelationshipType('CONTAINS');
      } else if (source.includes('SUPPLIER') || source.includes('VENDOR')) {
        setRelationshipType('SUPPLIES');
      } else if (target.includes('LOCATION') || target.includes('WAREHOUSE')) {
        setRelationshipType('STORED_AT');
      }
    }
  }, [sourceNode, targetNode, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!relationshipType.trim()) {
      return;
    }

    onConfirm({
      relationshipType: relationshipType.toUpperCase().replace(/\s+/g, '_'),
      cardinality,
      description: description || `${sourceNode?.label} ${relationshipType} ${targetNode?.label}`,
    });
  };

  const handleSuggestionClick = (type: string) => {
    setRelationshipType(type);
    setShowSuggestions(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 bg-neutral-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Link2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Add Relationship</h2>
              <p className="text-sm text-neutral-500">Define a new connection between nodes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-neutral-200 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Connection Preview */}
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-emerald-50 border-b border-neutral-200">
          <div className="flex items-center justify-center gap-3">
            <div className="px-3 py-2 bg-white rounded-lg border border-blue-200 shadow-sm">
              <span className="font-medium text-blue-700">{sourceNode?.label || 'Source'}</span>
            </div>
            <ArrowRight className="w-5 h-5 text-emerald-500" />
            <div className="px-3 py-2 bg-white rounded-lg border border-emerald-200 shadow-sm">
              <span className="font-medium text-emerald-700">{targetNode?.label || 'Target'}</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Relationship Type */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Relationship Type <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={relationshipType}
                onChange={(e) => setRelationshipType(e.target.value.toUpperCase())}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                placeholder="e.g., HAS, BELONGS_TO, CONTAINS"
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 uppercase"
                autoFocus
              />

              {/* Suggestions Dropdown */}
              {showSuggestions && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {COMMON_RELATIONSHIP_TYPES.filter(type =>
                    type.includes(relationshipType.toUpperCase())
                  ).map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleSuggestionClick(type)}
                      className="w-full px-4 py-2 text-left hover:bg-neutral-50 text-sm font-mono"
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Cardinality */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Cardinality
            </label>
            <div className="grid grid-cols-4 gap-2">
              {CARDINALITY_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setCardinality(option.value as NewRelationshipData['cardinality'])}
                  className={`px-3 py-2 rounded-lg border text-center transition-colors ${
                    cardinality === option.value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-neutral-200 hover:border-neutral-300 text-neutral-600'
                  }`}
                >
                  <div className="font-mono font-bold text-sm">{option.label}</div>
                  <div className="text-[10px] text-neutral-500">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-700">
              Description <span className="text-neutral-400">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this relationship..."
              rows={2}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!relationshipType.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Add Relationship
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRelationshipModal;
